/**
 * Vehicle equipment on the Assets tab: tail-lift, ADR, Moffett, small vehicle
 * (yes / no) and pallet spaces. No source system sends these, so values are
 * entered here (Asserted, saved as VehicleAttributesDeclared).
 *
 * Tail-lift, ADR, Payload (T) (the weight class) and Pallet spaces can also
 * carry a suggestion, worked out from job history and the source's vehicle record
 * (VehicleCapabilitiesInferred, provenance class Inferred). A suggestion is
 * only shown in an empty cell and is never saved as the vehicle's value on its
 * own: a person fills it in (one cell, or "Fill in suggestions") and saves.
 */

import { parseTypicalTonnes } from './assetPayloadTonnes';

/** An Assets roster row (same shape as Assets.tsx VehicleDetailsRow). */
type VehicleDetailsRow = Record<string, string | number>;

export const YES_NO_CAPABILITY_FIELDS = ['tail_lift', 'adr_equipped', 'moffett', 'small_vehicle'] as const;
export type YesNoCapabilityField = (typeof YES_NO_CAPABILITY_FIELDS)[number];
export type CapabilityField = YesNoCapabilityField | 'pallet_spaces';

export const CAPABILITY_FIELD_LABELS: Record<CapabilityField, string> = {
  tail_lift: 'Tail-lift',
  adr_equipped: 'ADR',
  moffett: 'Moffett',
  small_vehicle: 'Small vehicle',
  pallet_spaces: 'Pallet spaces',
};

export const CAPABILITY_FIELD_TITLES: Record<CapabilityField, string> = {
  tail_lift: 'Has a tail-lift (for an artic, set it on the trailer)',
  adr_equipped: 'Equipped to carry ADR (dangerous goods) loads',
  moffett: 'Carries a Moffett truck-mounted forklift',
  small_vehicle: 'Fits restricted-access drops (customer "Small Vehicle" requirement)',
  pallet_spaces: 'Pallet spaces on the deck (whole number)',
};

export const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const;

export type YesNo = 'yes' | 'no';

/** 'yes' / 'no', or '' when not set or unrecognised. */
export function formatYesNo(value: unknown): YesNo | '' {
  const s = String(value ?? '').trim().toLowerCase();
  return s === 'yes' || s === 'no' ? s : '';
}

export function yesNoLabel(value: unknown): string {
  const v = formatYesNo(value);
  return v === 'yes' ? 'Yes' : v === 'no' ? 'No' : '';
}

/** Whole number 1–60, or '' (not set). Same range the save route keeps. */
export function formatPalletSpaces(value: unknown): string {
  const s = String(value ?? '').trim();
  if (!/^\d+$/.test(s)) return '';
  const n = Number(s);
  return n >= 1 && n <= 60 ? String(n) : '';
}

/** Assets fields that can show a suggestion. */
export const SUGGESTABLE_FIELDS = [...YES_NO_CAPABILITY_FIELDS, 'typical_tonnes', 'pallet_spaces'] as const;
export type SuggestableField = (typeof SUGGESTABLE_FIELDS)[number];

export interface CapabilitySuggestion {
  /** 'yes' / 'no', a weight class in tonnes, or a whole number of pallet spaces. */
  value?: string;
  confidence: 'High' | 'Medium' | 'Low';
  evidence: string;
}

/** The vehicles-list `inferredCapabilities` field (VehicleCapabilitiesInferredPayload). */
export interface InferredCapabilities {
  registration?: string;
  rule_version?: string;
  evidence_source?: string;
  window_from?: string;
  window_to?: string;
  suggestions?: Partial<Record<SuggestableField, CapabilitySuggestion>>;
}

/** A field's value in its stored form, or '' when not set. */
function normaliseFieldValue(field: SuggestableField, value: unknown): string {
  if (field === 'typical_tonnes') return parseTypicalTonnes(String(value ?? ''));
  if (field === 'pallet_spaces') return formatPalletSpaces(value);
  return formatYesNo(value);
}

/** How a suggested value reads in a cell and its hover text. */
export function suggestionValueLabel(field: SuggestableField, value: string): string {
  if (field === 'typical_tonnes') return `${value}`;
  if (field === 'pallet_spaces') return value;
  return yesNoLabel(value);
}

export function suggestionFor(
  inferred: InferredCapabilities | undefined,
  field: SuggestableField,
): CapabilitySuggestion | undefined {
  const s = inferred?.suggestions?.[field];
  if (!s || typeof s.evidence !== 'string') return undefined;
  const value = normaliseFieldValue(field, s.value);
  return { confidence: s.confidence, evidence: s.evidence, ...(value ? { value } : {}) };
}

/** Hover text for a cell showing a suggestion. */
export function suggestionTitle(field: SuggestableField, s: CapabilitySuggestion): string {
  const head = s.value
    ? `Inferred (${s.confidence} confidence), not saved: suggests ${suggestionValueLabel(field, s.value)}${field === 'typical_tonnes' ? ' T' : ''}.`
    : 'Inferred: no suggestion.';
  const tail = s.value ? ' Edit the cell, or use Fill in suggestions and Save, to keep it.' : '';
  return `${head} ${s.evidence}${tail}`;
}

/**
 * Copy suggested values into empty cells of the given rows. Cells that
 * already hold a value are never changed. Returns the new rows and how many
 * cells were filled.
 */
export function fillSuggestedCapabilities(
  rows: VehicleDetailsRow[],
  rowIndices: Iterable<number>,
  inferredByReg: Map<string, InferredCapabilities>,
): { rows: VehicleDetailsRow[]; filled: number } {
  const next = [...rows];
  let filled = 0;
  for (const idx of rowIndices) {
    const row = next[idx];
    if (!row) continue;
    const inferred = inferredByReg.get(String(row.vehicle_reg ?? '').trim());
    let updated: VehicleDetailsRow | null = null;
    for (const field of SUGGESTABLE_FIELDS) {
      if (normaliseFieldValue(field, row[field])) continue;
      const value = suggestionFor(inferred, field)?.value;
      if (!value) continue;
      updated = { ...(updated ?? row), [field]: value };
      filled++;
    }
    if (updated) next[idx] = updated;
  }
  return { rows: next, filled };
}

/** Whether any of these rows has an empty cell a suggestion could fill. */
export function hasFillableSuggestions(
  rows: VehicleDetailsRow[],
  inferredByReg: Map<string, InferredCapabilities>,
): boolean {
  return rows.some((row) => {
    const inferred = inferredByReg.get(String(row.vehicle_reg ?? '').trim());
    return SUGGESTABLE_FIELDS.some((f) => !normaliseFieldValue(f, row[f]) && Boolean(suggestionFor(inferred, f)?.value));
  });
}
